import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  Flex,
  Box,
  Grid,
  Image,
  Circle,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import regularPassImage from '../../../../assets/images/design-guide/regular-pass.png';
import enhancedPassImage from '../../../../assets/images/design-guide/enhanced-pass.png';

interface DesignTip {
  number: string;
  title: string;
  description: string;
}

const REGULAR_TIPS: DesignTip[] = [
  {
    number: '01',
    title: 'High contrast colours',
    description:
      'Passes are scanned outdoors. Make sure background vs text passes a readability check before publishing.',
  },
  {
    number: '02',
    title: 'Field text',
    description:
      'Truncation depends on how many fields share a row. When in doubt, shorter is always safer.',
  },
  {
    number: '03',
    title: 'Front vs additional fields',
    description:
      'The front should show only what someone needs at a glance. Move the rest to additional Fields.',
  },
  {
    number: '04',
    title: 'Always preview on both platforms',
    description:
      'Apple Wallet and Google Wallet render passes differently. Always check both before publishing.',
  },
];

const ENHANCED_TIPS: DesignTip[] = [
  {
    number: '01',
    title: 'Artwork',
    description:
      "Your existing landscape banner won't work here. Use portrait photography or reformat your image before uploading.",
  },
  {
    number: '02',
    title: 'Quick actions',
    description:
      'Put your most important links first as they appear as primary buttons. These can be web links, booking portals, stores or PDFs',
  },
  {
    number: '03',
    title: 'Maps and weather',
    description:
      'Add your venue location and Apple pulls in directions and a live weather forecast automatically.',
  },
  {
    number: '04',
    title: 'Doors and kick-off',
    description:
      'Add geofencing or iBeacons in Features to send perfectly timed notifications as fans arrive at the venue.',
  },
];

const VIEWS = [
  {
    image: regularPassImage,
    alt: 'Regular pass design guide',
    tips: REGULAR_TIPS,
  },
  {
    image: enhancedPassImage,
    alt: 'Enhanced pass design guide',
    tips: ENHANCED_TIPS,
  },
];

const TipCard = ({ tip, minH }: { tip: DesignTip; minH?: string }) => (
  <Box
    bg="#3A4A57"
    borderRadius="10px"
    px="30px"
    pt="20px"
    pb="20px"
    minH={minH}
  >
    <Text
      fontFamily="Inter SemiBold, sans-serif"
      fontSize="15px"
      color="white"
      mb={3}
    >
      <Text as="span" color="#FF4935" mr={2}>
        {tip.number}
      </Text>
      {tip.title}
    </Text>
    <Text fontSize="13px" color="white" lineHeight="1.5">
      {tip.description}
    </Text>
  </Box>
);

interface DesignGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DesignGuideModal = ({ isOpen, onClose }: DesignGuideModalProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { tips } = VIEWS[activeIndex];

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || el.offsetWidth === 0) return;
    const index = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveIndex(index);
  }, []);

  const scrollTo = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' });
  }, []);

  const handleClick = useCallback(() => {
    const next = activeIndex < VIEWS.length - 1 ? activeIndex + 1 : 0;
    scrollTo(next);
  }, [activeIndex, scrollTo]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      blockScrollOnMount
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="15px"
        px={10}
        pt={12}
        pb={10}
        bg="#243744"
        maxH="98vh"
        mt="1vh"
      >
        <ModalCloseButton
          color="white"
          top={5}
          _hover={{ bg: 'transparent', color: '#D9D9D9' }}
        />
        <ModalBody p={0} overflowX="hidden">
          <Text textStyle="heading3" fontSize="34px" color="white" mb={2}>
            Design Guide
          </Text>

          <Box mb={8}>
            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              onClick={handleClick}
              borderRadius="8px"
              cursor="pointer"
              sx={{
                overflowX: 'scroll',
                overflowY: 'hidden',
                overscrollBehaviorX: 'contain',
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                position: 'relative',
                zIndex: 1,
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              <Flex w={`${VIEWS.length * 100}%`}>
                {VIEWS.map((view, i) => (
                  <Image
                    key={i}
                    src={view.image}
                    alt={view.alt}
                    w={`${100 / VIEWS.length}%`}
                    flexShrink={0}
                    draggable={false}
                    sx={{ scrollSnapAlign: 'start' }}
                  />
                ))}
              </Flex>
            </Box>
            <Flex justify="center" gap={2} mt={-7}>
              {VIEWS.map((_, i) => (
                <Circle
                  key={i}
                  size="6px"
                  bg={i === activeIndex ? '#FF4935' : '#E1E1E1'}
                  cursor="pointer"
                  aria-label={`View ${i + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollTo(i);
                  }}
                />
              ))}
            </Flex>
            {activeIndex === 1 && (
              <Text fontSize="12px" color="#D9D9D9" textAlign="center" mt={6}>
                Available on iOS 18 and later.
                <br />
                Older devices will show a standard Apple event ticket
              </Text>
            )}
          </Box>

          <Grid
            templateColumns="1fr 1fr"
            gap={4}
            mt={activeIndex === 0 ? 24 : 4}
          >
            {tips.map((tip) => (
              <TipCard
                key={tip.number}
                tip={tip}
                minH={activeIndex === 0 ? '130px' : undefined}
              />
            ))}
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DesignGuideModal;
